import orderBy from 'lodash.orderby'
import { createAsyncThunk, createSlice, PayloadAction, SliceCaseReducers, ValidateSliceCaseReducers } from '@reduxjs/toolkit'
import { medicareApiClient } from 'clients/medicareApiClient'

import { MspSortDirection, MspSortType } from 'models/claims'
import { MSPCase, MSPRequest, MSPResponse } from '@cms-max/medicare-api-sdk'
import { RootState } from 'redux/config'
import { cookieInterfaces } from 'utils/cookies'
import { slsClient } from 'clients/slsClient'
import { determineCurrentLanguageFromUrl } from 'utils/language'

export enum Status {
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
  FINISHED = 'finished'
}

export const SEARCH_RESULTS_PAGE_SIZE = 10

export type ClaimCaseReducer = {
  response?: MSPResponse
  results: MSPCase[]
  searchResultsPage: number
  sortType: MspSortType
  sortDirection: MspSortDirection
  status: Status
}

type SortClaimCaseAction = {
  sortType: MspSortType
  sortDirection: MspSortDirection
}

export const createClaimCaseGetters = (classType: 'msp' | 'msa') => {
  return {
    getStatus: (state: RootState) => state[classType].status,
    getAllSearchResults: (state: RootState) => state[classType].results,
    getSearchResultsTotal: (state: RootState) => state[classType].results.length,
    getSearchResults: (state: RootState) => {
      const results = state[classType].results
      const end = state[classType].searchResultsPage * SEARCH_RESULTS_PAGE_SIZE
      return results.slice(0, end)
    },
    getSortType: (state: RootState) => state[classType].sortType,
    getSortDirection: (state: RootState) => state[classType].sortDirection
  }
}

// Sort a list of claims by the given sort type.
export function doSortCases (cases: MSPCase[], sortType: MspSortType, direction: MspSortDirection): MSPCase[] {
  switch (sortType) {
    case MspSortType.CASE_TYPE:
      return orderBy(cases, (c) => c.caseType, direction)
    case MspSortType.CASE_ID:
      return orderBy(cases, (c) => c.caseID, direction)
    case MspSortType.INCIDENT_DATE:
      return orderBy(cases, (c) => new Date(c.caseDate), direction)
    default:
      return cases
  }
}

export const fetchMspSummaries = createAsyncThunk(
  'claims/fetchMspSummaries',
  async ({ classType, useOnlySlsJwtForAuthByDefault }: MSPRequest & { useOnlySlsJwtForAuthByDefault: boolean }, { dispatch }) => {
    // We need to fetch reqToken, so that core has it when it fetches this data from backend services
    if (useOnlySlsJwtForAuthByDefault && !cookieInterfaces.fetchedReqToken.isSet()) {
      const currentUrl = window.location.href.toLowerCase()
      const currentLanguage = determineCurrentLanguageFromUrl(currentUrl)
      const reqTokenResponse = await slsClient.getReqToken(currentLanguage)
      await medicareApiClient.asyncGetBeneficiaryByReqToken(reqTokenResponse.req_token)
      cookieInterfaces.fetchedReqToken.set('true')
    }

    const response = await medicareApiClient.asyncFetchMspSummaries({ classType })
    if (response) {
      return response
    } else {
      throw new Error('Error fetching claims results')
    }
  }
)

export const createClaimCaseSlice = <
  Reducers extends SliceCaseReducers<ClaimCaseReducer>
>({
    name,
    initialState,
    reducers
  }: {
  name: 'msp' | 'msa'
  initialState: ClaimCaseReducer
  reducers: ValidateSliceCaseReducers<ClaimCaseReducer, Reducers>
}) => {
  return createSlice({
    name,
    initialState,
    reducers: {
      showMoreSearchResults: (state) => {
        state.searchResultsPage += 1
      },
      showAllSearchResults: (state) => {
        const total = state.results.length
        state.searchResultsPage = Math.ceil(total / SEARCH_RESULTS_PAGE_SIZE)
      },
      sortCases: (state, action: PayloadAction<SortClaimCaseAction>) => {
        const { sortType, sortDirection } = action.payload
        state.sortType = sortType
        state.results = doSortCases(state.results, sortType, sortDirection)
      },
      ...reducers
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchMspSummaries.pending, (state: ClaimCaseReducer) => {
          state.status = Status.LOADING
        })
        .addCase(fetchMspSummaries.fulfilled, (state: ClaimCaseReducer, { payload }) => {
          state.status = Status.FINISHED
          state.results = doSortCases(payload.MSPList, state.sortType, state.sortDirection)
          state.searchResultsPage = 1
        })
        .addCase(fetchMspSummaries.rejected, (state: ClaimCaseReducer) => {
          state.status = Status.ERROR
        })
    }
  })
}
